/**
 * MHARHER Profile Page - Complete Redesign
 * Professional profile for 1M+ users app
 * Sidi Bou Said theme (Blue/White/Harissa Red)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, Calendar, ChefHat, Settings, UserPlus, UserMinus, BadgeCheck,
  Flame, Trophy, Star, Heart, MessageCircle, Share2, MoreHorizontal,
  Grid3X3, Play, Bookmark, Award, TrendingUp, Clock, Eye, Utensils,
  Instagram, Youtube, ExternalLink, Copy, Check, Camera, Edit3,
  Sparkles, Crown, Medal, Target, Zap, Users, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { fr, ar, enUS } from 'date-fns/locale';

// Level configuration with colors and icons
const LEVELS = {
  'apprenti': { name: 'Apprenti', color: 'from-gray-400 to-gray-500', icon: '🌱', min: 0 },
  'cuisinier': { name: 'Cuisinier', color: 'from-green-400 to-green-600', icon: '🥄', min: 100 },
  'chef': { name: 'Chef', color: 'from-blue-400 to-blue-600', icon: '👨‍🍳', min: 500 },
  'chef_etoile': { name: 'Chef Étoilé', color: 'from-purple-400 to-purple-600', icon: '⭐', min: 1500 },
  'maitre_chef': { name: 'Maître Chef', color: 'from-yellow-400 to-yellow-600', icon: '👑', min: 5000 },
  'legende': { name: 'Légende', color: 'from-red-500 to-orange-500', icon: '🔥', min: 15000 },
};

// Badge definitions
const BADGE_ICONS = {
  'creator': { icon: Crown, color: 'text-yellow-500', bg: 'bg-yellow-100' },
  'verified': { icon: BadgeCheck, color: 'text-blue-500', bg: 'bg-blue-100' },
  'top_chef': { icon: Trophy, color: 'text-amber-500', bg: 'bg-amber-100' },
  'challenger': { icon: Target, color: 'text-purple-500', bg: 'bg-purple-100' },
  'popular': { icon: Heart, color: 'text-red-500', bg: 'bg-red-100' },
  'streak_7': { icon: Zap, color: 'text-orange-500', bg: 'bg-orange-100' },
  'streak_30': { icon: Flame, color: 'text-red-500', bg: 'bg-red-100' },
  'first_recipe': { icon: Utensils, color: 'text-green-500', bg: 'bg-green-100' },
};

function ProfilePage() {
  const { username } = useParams();
  const { api, user: currentUser } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('posts');
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  const getLocale = () => {
    switch (i18n.language) {
      case 'fr': return fr;
      case 'ar': return ar;
      default: return enUS;
    }
  };

  // Load profile data
  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      try {
        // First get the profile
        const profileRes = await api.get(`/users/${username}`);
        setProfile(profileRes.data);
        
        // Then get posts using the user ID
        try {
          const postsRes = await api.get(`/posts/user/${profileRes.data.id}?limit=20`);
          setPosts(postsRes.data.posts || []);
        } catch (e) {
          setPosts([]);
        }
        
        // Get user's recipes
        try {
          const recipesRes = await api.get(`/recipes?creator_id=${profileRes.data.id}&limit=20`);
          setRecipes(recipesRes.data || []);
        } catch (e) {
          setRecipes([]);
        }
        
        // Get saved recipes (only for own profile)
        if (currentUser && currentUser.username === username) {
          try {
            const savedRes = await api.get('/recipes/saved');
            setSavedRecipes(savedRes.data.recipes || []);
          } catch (e) {
            setSavedRecipes([]);
          }
        }
        
        // Check if following
        if (currentUser && currentUser.username !== username) {
          try {
            const followersRes = await api.get(`/users/${profileRes.data.id}/followers`);
            setIsFollowing(followersRes.data.some(f => f.id === currentUser.id));
          } catch (e) {}
        }
      } catch (e) {
        toast.error(t('errors.profileNotFound', 'Profil non trouvé'));
        navigate('/home');
      } finally {
        setLoading(false);
      }
    };
    
    if (username) loadProfile();
  }, [username, api, currentUser, navigate, t]);

  // Handle follow/unfollow
  const handleFollow = async () => {
    if (!profile || followLoading) return;
    setFollowLoading(true);
    
    try {
      if (isFollowing) {
        await api.delete(`/users/${profile.id}/follow`);
        setIsFollowing(false);
        setProfile(p => ({ ...p, followers_count: Math.max(0, (p.followers_count || 0) - 1) }));
        toast.success(t('profile.unfollowed', 'Désabonné'));
      } else {
        await api.post(`/users/${profile.id}/follow`);
        setIsFollowing(true);
        setProfile(p => ({ ...p, followers_count: (p.followers_count || 0) + 1 }));
        toast.success(t('profile.followed', 'Abonné!'));
      }
    } catch (e) {
      toast.error(t('errors.generic'));
    } finally {
      setFollowLoading(false);
    }
  };

  // Copy profile link
  const copyProfileLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success(t('profile.linkCopied', 'Lien copié!'));
    setTimeout(() => setCopied(false), 2000);
  };

  // Share profile
  const shareProfile = async () => {
    if (navigator.share) {
      await navigator.share({
        title: `${profile.display_name} sur MHARHER`,
        text: `Découvre le profil de ${profile.display_name} sur MHARHER!`,
        url: window.location.href
      });
    } else {
      copyProfileLink();
    }
  };

  // Calculate level progress
  const getLevelProgress = () => {
    if (!profile) return { progress: 0, currentLevel: LEVELS['apprenti'], nextLevel: LEVELS['cuisinier'] };
    
    const barka = profile.barka || 0;
    const levelKeys = Object.keys(LEVELS);
    let currentLevelKey = 'apprenti';
    let nextLevelKey = 'cuisinier';
    
    for (let i = levelKeys.length - 1; i >= 0; i--) {
      if (barka >= LEVELS[levelKeys[i]].min) {
        currentLevelKey = levelKeys[i];
        nextLevelKey = levelKeys[Math.min(i + 1, levelKeys.length - 1)];
        break;
      }
    }
    
    const currentLevel = LEVELS[currentLevelKey];
    const nextLevel = LEVELS[nextLevelKey];
    const progress = nextLevelKey === currentLevelKey 
      ? 100 
      : ((barka - currentLevel.min) / (nextLevel.min - currentLevel.min)) * 100;
    
    return { progress: Math.min(100, Math.max(0, progress)), currentLevel, nextLevel, barka };
  };

  if (loading) {
    return <ProfileSkeleton />;
  }

  if (!profile) return null;

  const isOwn = currentUser?.username === username;
  const levelInfo = getLevelProgress();

  return (
    <div className="min-h-screen bg-[#F8F5F2]" data-testid="profile-page">
      {/* Hero Section with Cover */}
      <div className="relative">
        {/* Cover Image */}
        <div className="h-48 md:h-64 lg:h-72 relative overflow-hidden">
          {profile.cover_image && !profile.cover_image.includes('base64') ? (
            <img 
              src={profile.cover_image} 
              alt="Cover" 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#1E4ED8] via-[#3B82F6] to-[#0D3A9E]">
              {/* Decorative pattern */}
              <div className="absolute inset-0 opacity-10">
                <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                    <circle cx="5" cy="5" r="1" fill="white"/>
                  </pattern>
                  <rect width="100%" height="100%" fill="url(#grid)"/>
                </svg>
              </div>
            </div>
          )}
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          
          {/* Edit cover button (own profile) */}
          {isOwn && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-4 right-4 bg-black/30 hover:bg-black/50 text-white backdrop-blur-sm"
              onClick={() => navigate('/settings')}
            >
              <Camera className="w-4 h-4 mr-2" />
              {t('profile.editCover', 'Modifier')}
            </Button>
          )}
        </div>

        {/* Profile Info Overlay */}
        <div className="max-w-5xl mx-auto px-4 relative">
          <div className="flex flex-col md:flex-row md:items-end gap-4 -mt-16 md:-mt-20">
            {/* Avatar */}
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="relative z-10"
            >
              <div className={`w-32 h-32 md:w-40 md:h-40 rounded-full p-1 bg-gradient-to-br ${levelInfo.currentLevel.color} shadow-2xl`}>
                <Avatar className="w-full h-full border-4 border-white">
                  <AvatarImage src={profile.profile_picture} className="object-cover" />
                  <AvatarFallback className="text-4xl md:text-5xl font-bold bg-white text-[#1E4ED8]">
                    {profile.display_name?.charAt(0) || profile.username?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </div>
              
              {/* Level badge */}
              <div className="absolute -bottom-2 -right-2 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-2xl border-2 border-gray-100">
                {levelInfo.currentLevel.icon}
              </div>
              
              {/* Verified badge */}
              {profile.is_verified && (
                <div className="absolute top-2 right-2 w-8 h-8 bg-[#1E4ED8] rounded-full flex items-center justify-center shadow-lg">
                  <BadgeCheck className="w-5 h-5 text-white" />
                </div>
              )}
            </motion.div>

            {/* Name and actions */}
            <div className="flex-1 pb-4">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h1 className="font-fraunces text-2xl md:text-3xl font-bold text-gray-900">
                  {profile.display_name}
                </h1>
                {profile.is_creator && (
                  <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white border-0">
                    <Crown className="w-3 h-3 mr-1" />
                    Créateur
                  </Badge>
                )}
              </div>
              <p className="text-gray-500 mb-2">@{profile.username}</p>
              
              {/* Location and join date */}
              <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                {profile.city && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {profile.city}, {profile.country || 'Tunisie'}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {t('profile.joined', 'Membre depuis')} {format(new Date(profile.created_at), 'MMMM yyyy', { locale: getLocale() })}
                </span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex gap-2 pb-4">
              {isOwn ? (
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/settings')}
                  className="rounded-full"
                  data-testid="edit-profile-btn"
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  {t('profile.editProfile', 'Modifier le profil')}
                </Button>
              ) : (
                <>
                  <Button 
                    onClick={handleFollow}
                    disabled={followLoading}
                    className={`rounded-full px-6 ${
                      isFollowing 
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                        : 'bg-[#1E4ED8] hover:bg-[#1842B8] text-white'
                    }`}
                    data-testid="follow-btn"
                  >
                    {isFollowing ? (
                      <><UserMinus className="w-4 h-4 mr-2" />{t('common.following', 'Abonné')}</>
                    ) : (
                      <><UserPlus className="w-4 h-4 mr-2" />{t('common.follow', 'Suivre')}</>
                    )}
                  </Button>
                  <Button 
                    variant="outline" 
                    className="rounded-full"
                    onClick={() => navigate(`/messages/${profile.id}`)}
                  >
                    <MessageCircle className="w-4 h-4" />
                  </Button>
                </>
              )}
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="rounded-full">
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={shareProfile}>
                    <Share2 className="w-4 h-4 mr-2" />
                    {t('common.share', 'Partager')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={copyProfileLink}>
                    {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                    {t('profile.copyLink', 'Copier le lien')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Bio */}
        {profile.bio && (
          <motion.p 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-gray-700 mb-6 max-w-2xl"
          >
            {profile.bio}
          </motion.p>
        )}

        {/* Stats Cards - Bento Style */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard 
            value={profile.followers_count || 0}
            label={t('common.followers', 'Abonnés')}
            icon={Users}
            color="blue"
            onClick={() => {}}
          />
          <StatCard 
            value={profile.following_count || 0}
            label={t('common.following', 'Abonnements')}
            icon={UserPlus}
            color="gray"
            onClick={() => {}}
          />
          <StatCard 
            value={profile.barka || 0}
            label="BARKA"
            icon={Flame}
            color="orange"
            highlight
          />
          <StatCard 
            value={profile.recipes_completed || 0}
            label={t('profile.recipes', 'Recettes')}
            icon={Utensils}
            color="green"
          />
        </div>

        {/* Level Progress Card */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${levelInfo.currentLevel.color} flex items-center justify-center text-2xl shadow-lg`}>
                {levelInfo.currentLevel.icon}
              </div>
              <div>
                <p className="font-bold text-lg text-gray-900">{levelInfo.currentLevel.name}</p>
                <p className="text-sm text-gray-500">{profile.barka || 0} BARKA</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">{t('profile.nextLevel', 'Prochain niveau')}</p>
              <p className="font-bold text-[#1E4ED8]">{levelInfo.nextLevel.name} {levelInfo.nextLevel.icon}</p>
            </div>
          </div>
          
          <div className="relative">
            <Progress value={levelInfo.progress} className="h-3 bg-gray-100" />
            <div 
              className="absolute top-0 left-0 h-3 rounded-full bg-gradient-to-r from-[#1E4ED8] to-[#3B82F6] transition-all duration-500"
              style={{ width: `${levelInfo.progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-2 text-center">
            {Math.round(levelInfo.progress)}% {t('profile.toNextLevel', 'vers le prochain niveau')}
          </p>
        </motion.div>

        {/* Badges Section */}
        {profile.badges && profile.badges.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-6"
          >
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Award className="w-5 h-5 text-[#1E4ED8]" />
              {t('profile.badges', 'Badges')}
            </h3>
            <div className="flex flex-wrap gap-2">
              {profile.badges.map((badge, idx) => {
                const badgeInfo = BADGE_ICONS[badge] || { icon: Star, color: 'text-gray-500', bg: 'bg-gray-100' };
                const BadgeIcon = badgeInfo.icon;
                return (
                  <div 
                    key={idx}
                    className={`flex items-center gap-2 px-3 py-2 rounded-full ${badgeInfo.bg} ${badgeInfo.color}`}
                  >
                    <BadgeIcon className="w-4 h-4" />
                    <span className="text-sm font-medium capitalize">{badge.replace('_', ' ')}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Social Links */}
        {(profile.tiktok_username || profile.instagram_username || profile.youtube_username) && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-wrap gap-3 mb-6"
          >
            {profile.tiktok_username && (
              <a 
                href={`https://tiktok.com/@${profile.tiktok_username}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-full hover:bg-gray-800 transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                </svg>
                @{profile.tiktok_username}
              </a>
            )}
            {profile.instagram_username && (
              <a 
                href={`https://instagram.com/${profile.instagram_username}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full hover:opacity-90 transition-opacity"
              >
                <Instagram className="w-4 h-4" />
                @{profile.instagram_username}
              </a>
            )}
            {profile.youtube_username && (
              <a 
                href={`https://youtube.com/@${profile.youtube_username}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
              >
                <Youtube className="w-4 h-4" />
                @{profile.youtube_username}
              </a>
            )}
          </motion.div>
        )}

        {/* Creator Stats (if creator) */}
        {profile.is_creator && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-5 border border-yellow-200 mb-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <Crown className="w-5 h-5 text-yellow-600" />
              <h3 className="font-semibold text-yellow-800">{t('profile.creatorStats', 'Stats Créateur')}</h3>
              <Badge className="bg-yellow-200 text-yellow-800 border-0 capitalize">
                {profile.creator_tier || 'Bronze'}
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-700">{profile.challenges_won || 0}</p>
                <p className="text-xs text-yellow-600">{t('profile.challengesWon', 'Défis gagnés')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-700">{profile.recipes_created || 0}</p>
                <p className="text-xs text-yellow-600">{t('profile.recipesCreated', 'Recettes créées')}</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-700">{profile.streak_days || 0}</p>
                <p className="text-xs text-yellow-600">{t('profile.streakDays', 'Jours de streak')}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full justify-start bg-white rounded-xl p-1 border border-gray-100 mb-4">
            <TabsTrigger 
              value="posts" 
              className="flex-1 data-[state=active]:bg-[#1E4ED8] data-[state=active]:text-white rounded-lg"
            >
              <Grid3X3 className="w-4 h-4 mr-2" />
              {t('profile.posts', 'Posts')}
            </TabsTrigger>
            <TabsTrigger 
              value="videos" 
              className="flex-1 data-[state=active]:bg-[#1E4ED8] data-[state=active]:text-white rounded-lg"
            >
              <Play className="w-4 h-4 mr-2" />
              {t('profile.videos', 'Vidéos')}
            </TabsTrigger>
            <TabsTrigger 
              value="recipes" 
              className="flex-1 data-[state=active]:bg-[#1E4ED8] data-[state=active]:text-white rounded-lg"
            >
              <Utensils className="w-4 h-4 mr-2" />
              {t('profile.recipes', 'Recettes')}
            </TabsTrigger>
            {isOwn && (
              <TabsTrigger 
                value="saved" 
                className="flex-1 data-[state=active]:bg-[#1E4ED8] data-[state=active]:text-white rounded-lg"
              >
                <Bookmark className="w-4 h-4 mr-2" />
                {t('profile.saved', 'Sauvegardés')}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="posts">
            <ContentGrid items={posts} type="posts" emptyMessage={t('profile.noPosts', 'Aucun post encore')} />
          </TabsContent>
          
          <TabsContent value="videos">
            <ContentGrid 
              items={posts.filter(p => p.media_urls?.some(url => url.includes('video')))} 
              type="videos" 
              emptyMessage={t('profile.noVideos', 'Aucune vidéo encore')} 
            />
          </TabsContent>
          
          <TabsContent value="recipes">
            <ContentGrid items={recipes} type="recipes" emptyMessage={t('profile.noRecipes', 'Aucune recette encore')} />
          </TabsContent>
          
          {isOwn && (
            <TabsContent value="saved">
              <ContentGrid items={savedRecipes} type="saved" emptyMessage={t('profile.noSaved', 'Aucun contenu sauvegardé')} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}

// Stat Card Component
function StatCard({ value, label, icon: Icon, color, highlight, onClick }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    gray: 'bg-gray-50 text-gray-600 border-gray-100',
    orange: 'bg-orange-50 text-orange-600 border-orange-100',
    green: 'bg-green-50 text-green-600 border-green-100',
  };

  return (
    <motion.div 
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`relative overflow-hidden rounded-xl p-4 border cursor-pointer transition-shadow hover:shadow-md ${
        highlight 
          ? 'bg-gradient-to-br from-orange-100 to-red-100 border-orange-200' 
          : colorClasses[color]
      }`}
    >
      {highlight && (
        <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-orange-300/30 to-red-300/30 rounded-full -mr-8 -mt-8" />
      )}
      <Icon className={`w-5 h-5 mb-2 ${highlight ? 'text-orange-600' : ''}`} />
      <p className="text-2xl font-bold">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      <p className="text-xs opacity-70">{label}</p>
    </motion.div>
  );
}

// Content Grid Component
function ContentGrid({ items, type, emptyMessage }) {
  if (!items || items.length === 0) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-white rounded-2xl p-12 text-center border border-gray-100"
      >
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
          {type === 'videos' ? <Play className="w-8 h-8 text-gray-400" /> :
           type === 'recipes' ? <Utensils className="w-8 h-8 text-gray-400" /> :
           type === 'saved' ? <Bookmark className="w-8 h-8 text-gray-400" /> :
           <Grid3X3 className="w-8 h-8 text-gray-400" />}
        </div>
        <p className="text-gray-500">{emptyMessage}</p>
      </motion.div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-1 md:gap-2">
      {items.map((item, idx) => {
        // Get image URL - handle both posts (media_urls) and recipes (image_url)
        const imageUrl = item.media_urls?.[0] || item.image_url;
        
        return (
          <motion.div
            key={item.id || idx}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: idx * 0.05 }}
            className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group cursor-pointer"
          >
            {imageUrl ? (
              <img 
                src={imageUrl} 
                alt={item.title || ""}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#1E4ED8]/10 to-[#3B82F6]/10 flex items-center justify-center">
                <Utensils className="w-8 h-8 text-gray-300" />
              </div>
            )}
            
            {/* Recipe title overlay for recipes/saved types */}
            {(type === 'recipes' || type === 'saved') && item.title && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                <p className="text-white text-xs font-medium line-clamp-1">{item.title}</p>
              </div>
            )}
            
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4 text-white">
              <span className="flex items-center gap-1">
                <Heart className="w-4 h-4" />
                {item.likes_count || 0}
              </span>
              {(type === 'posts' || type === 'videos') && (
                <span className="flex items-center gap-1">
                  <MessageCircle className="w-4 h-4" />
                  {item.comments_count || 0}
                </span>
              )}
              {(type === 'recipes' || type === 'saved') && (
                <span className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  {item.views_count || 0}
                </span>
              )}
            </div>
            
            {/* Video indicator */}
            {type === 'videos' && (
              <div className="absolute top-2 right-2">
                <Play className="w-5 h-5 text-white drop-shadow-lg" fill="white" />
              </div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}

// Loading Skeleton
function ProfileSkeleton() {
  return (
    <div className="min-h-screen bg-[#F8F5F2]">
      <Skeleton className="h-64 w-full" />
      <div className="max-w-5xl mx-auto px-4 -mt-16">
        <div className="flex gap-4">
          <Skeleton className="w-32 h-32 rounded-full" />
          <div className="flex-1 pt-20">
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-32 mb-4" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3 mt-6">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
